import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ButtonControl, DialogControl, FieldControl, FormControl, Loading } from "spark-core-dx/components";
import { useValidationErrors } from "spark-core-dx/hooks";
import { SystemTypes } from "spark-core-dx/services";
import { useResourceDomainContext } from "../../../ResourceDomainProvider";
import {
  applySetupFormFieldOverrides,
  buildResourceOperationQueryParams,
  buildSetupFormMetadata,
  buildSourceSystemDraft,
  buildSourceSystemStepValues,
  buildWizardValidationError,
  validateRequiredIntegrationFields,
  buildServerFailureError,
  getDisplayName,
  getFirst,
  getId,
  getIntegrationDatabaseName,
  getLinkedRecord,
  getLinkedRecordItemId,
  getMetadataFields,
  getOperationItem,
  getOperationItems,
  getOperationResult,
  getProductResourceDefaults,
  getSetupFormFieldOverrides,
  getValue,
  keepObjectStateWhenEqual,
  LOAD_SOURCE_SYSTEM_TARGETS_OPERATION,
  requireServiceResponse,
  requireSuccessfulServiceResponse,
  resolveProductResourceSourceSystemTypeValue,
  SOURCE_SYSTEM_EXISTING_FORM_GROUP_ID,
  SOURCE_SYSTEM_MODE_EXISTING,
  SOURCE_SYSTEM_MODE_NEW,
  SOURCE_SYSTEM_TYPE_EDI,
  SOURCE_SYSTEM_NEW_FORM_GROUP_ID
} from "../resourceConfigWizardUtils";

const SOURCE_SYSTEM_SETUP_HIDDEN_FIELD_NAMES = [
  "integrationid",
  "sourcesystemtype",
  "profileid",
  "sourceobjecttargets"
];

const SOURCE_SYSTEM_DEFAULTED_FIELDS = [
  "Name",
  "DisplayName",
  "SystemName",
  "Description",
  "TimeZone",
  "DefaultObjectUniqueIDSeparator"
];

const isBlankDraftValue = (value) => value === undefined || value === null || value === "";

const normalizeSourceSystemType = (value) => {
  if (value === undefined || value === null || value === "") return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : value;
};

const mergeDraftWithSeed = (previous, nextSeed, previousSeed, overrides) => {
  const nextDraft = {
    ...nextSeed,
    ...(previous || {}),
    ...overrides
  };

  SOURCE_SYSTEM_DEFAULTED_FIELDS.forEach((fieldName) => {
    const previousValue = previous?.[fieldName];
    if (isBlankDraftValue(previousValue) || previousValue === previousSeed?.[fieldName]) {
      nextDraft[fieldName] = nextSeed?.[fieldName];
    }
  });

  return nextDraft;
};

const SourceSystemStep = ({
  context,
  registerStepController
}) => {
  const { wizard, values = {}, readOnly } = context;
  const resourceDomainContext = useResourceDomainContext();
  const coreContext = resourceDomainContext?.coreContext;
  const coreState = resourceDomainContext?.coreState;
  const integrationMgr = resourceDomainContext?.managers?.integration;
  const sourceSystemMgr = resourceDomainContext?.managers?.sourceSystem;
  const integrationMgrReady = Boolean(integrationMgr);
  const sourceSystemMgrReady = Boolean(sourceSystemMgr);
  const { hasAndShowValidationErrors } = useValidationErrors();
  const integrationMgrRef = useRef(integrationMgr);
  const sourceSystemMgrRef = useRef(sourceSystemMgr);
  const coreStateRef = useRef(coreState);
  const loadKeyRef = useRef("");
  const confirmContinueRef = useRef(null);
  const prepareStepValuesRef = useRef(null);
  const isBusyRef = useRef(false);
  const productResourceDefaults = context.productResourceDefaults || getProductResourceDefaults(wizard);
  const sourceSystemTypeValue = resolveProductResourceSourceSystemTypeValue(productResourceDefaults);
  const sourceSystemTypeKey = getValue(productResourceDefaults, "sourceSystemTypeKey", "SourceSystemTypeKey") || "";
  const supportsExistingSourceSystemSelection = sourceSystemTypeValue === SOURCE_SYSTEM_TYPE_EDI || String(sourceSystemTypeKey || "").toUpperCase() === "EDI";
  const providerName = getValue(productResourceDefaults, "providerName", "ProviderName") || "Provider";
  const linkedIntegrationId = getLinkedRecordItemId(getLinkedRecord(wizard, "Integration", "SourceIntegration")) || values?.IntegrationId || "";
  const linkedSourceSystemId = getLinkedRecordItemId(getLinkedRecord(wizard, "SourceSystem", "SourceSystem")) || values?.SourceSystemId || "";
  const profileId = coreContext?.Profile?._id || coreContext?.Profile?.UniqueId || "";
  const [linkedIntegration, setLinkedIntegration] = useState(null);
  const [mode, setMode] = useState(() => (
    supportsExistingSourceSystemSelection && linkedSourceSystemId
      ? SOURCE_SYSTEM_MODE_EXISTING
      : SOURCE_SYSTEM_MODE_NEW
  ));
  const [sourceSystems, setSourceSystems] = useState([]);
  const [selectedSourceSystemId, setSelectedSourceSystemId] = useState(linkedSourceSystemId);
  const productResourceDefaultsKey = JSON.stringify(productResourceDefaults || {});
  const sourceSystemDraftValuesKey = JSON.stringify(values || {});
  const linkedIntegrationDraftKey = JSON.stringify({
    id: getId(linkedIntegration),
    displayName: getDisplayName(linkedIntegration),
    dbName: getIntegrationDatabaseName(linkedIntegration)
  });
  const sourceSystemDraftSeed = useMemo(
    () => buildSourceSystemDraft(values, productResourceDefaults, linkedIntegrationId, profileId, linkedIntegration),
    // The JSON keys are intentional: wizard values/defaults are recreated by the parent frequently.
    // Reloading from object identity causes this step to loop in loading state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [linkedIntegrationDraftKey, linkedIntegrationId, productResourceDefaultsKey, profileId, sourceSystemDraftValuesKey]
  );
  const sourceSystemDraftSeedRef = useRef(sourceSystemDraftSeed);
  const [draftSourceSystem, setDraftSourceSystem] = useState(() => sourceSystemDraftSeed);
  const [metadata, setMetadata] = useState(null);
  const [loadingSourceSystems, setLoadingSourceSystems] = useState(true);
  const [loadingTargets, setLoadingTargets] = useState(false);
  const [targetGenerationConfirm, setTargetGenerationConfirm] = useState(null);
  const [localError, setLocalError] = useState("");

  useEffect(() => {
    integrationMgrRef.current = integrationMgr;
  }, [integrationMgr]);

  useEffect(() => {
    sourceSystemMgrRef.current = sourceSystemMgr;
  }, [sourceSystemMgr]);

  useEffect(() => {
    coreStateRef.current = coreState;
  }, [coreState]);

  useEffect(() => {
    const previousSeed = sourceSystemDraftSeedRef.current;
    setDraftSourceSystem((previous) => {
      const nextDraft = mergeDraftWithSeed(previous, sourceSystemDraftSeed, previousSeed, {
        IsNew: !getId(previous),
        IntegrationId: linkedIntegrationId,
        SourceSystemType: sourceSystemTypeValue,
        ProfileId: previous?.ProfileId || profileId
      });
      return keepObjectStateWhenEqual(previous, nextDraft);
    });
    sourceSystemDraftSeedRef.current = sourceSystemDraftSeed;
  }, [linkedIntegrationId, profileId, sourceSystemDraftSeed, sourceSystemTypeValue]);

  useEffect(() => {
    let mounted = true;
    const activeMgr = sourceSystemMgrRef.current;
    const activeIntegrationMgr = integrationMgrRef.current;
    const loadKey = `${linkedIntegrationId || "none"}:${linkedSourceSystemId || "new"}:${sourceSystemTypeValue ?? "all"}:${profileId || ""}:${supportsExistingSourceSystemSelection ? "select" : "draft"}`;
    if (!sourceSystemMgrReady || !activeMgr) {
      setLoadingSourceSystems(false);
      setLocalError("Source System service is not available.");
      return undefined;
    }
    if (linkedIntegrationId && (!integrationMgrReady || !activeIntegrationMgr)) {
      setLoadingSourceSystems(false);
      setLocalError("Integration service is not available.");
      return undefined;
    }
    if (loadKeyRef.current === loadKey) return undefined;

    loadKeyRef.current = loadKey;
    setLoadingSourceSystems(true);
    setLocalError("");

    Promise.all([
      activeMgr.Query({ model: "x => x.IsDeleted == false" }),
      activeMgr.GetMetadata({}),
      linkedIntegrationId && activeIntegrationMgr
        ? activeIntegrationMgr.Query({ model: "x => x.IsDeleted == false" })
        : Promise.resolve({ Items: [] })
    ])
      .then(([queryResult, metadataResult, integrationResult]) => {
        if (!mounted) return;
        requireSuccessfulServiceResponse(queryResult, "Source System records could not be loaded.", coreStateRef.current);
        requireServiceResponse(metadataResult, "Source System metadata could not be loaded.");
        if (linkedIntegrationId) {
          requireSuccessfulServiceResponse(integrationResult, "Integration record could not be loaded.", coreStateRef.current);
        }

        const nextLinkedIntegration = linkedIntegrationId
          ? getOperationItems(integrationResult).find((integration) => String(getId(integration)).toLowerCase() === String(linkedIntegrationId).toLowerCase()) || null
          : null;
        setLinkedIntegration(nextLinkedIntegration);

        const nextDraftSeed = buildSourceSystemDraft(values, productResourceDefaults, linkedIntegrationId, profileId, nextLinkedIntegration);
        const previousSeed = sourceSystemDraftSeedRef.current || sourceSystemDraftSeed;
        sourceSystemDraftSeedRef.current = nextDraftSeed;
        setMetadata(getFirst(metadataResult?.Items) || getFirst(metadataResult) || getFirst(activeMgr.uiMetadata) || null);
        const loadedSourceSystems = getOperationItems(queryResult);
        const matchingSourceSystems = loadedSourceSystems.filter((sourceSystem) => {
          const sourceIntegrationId = getValue(sourceSystem, "IntegrationId", "integrationId") || "";
          const typeValue = normalizeSourceSystemType(getValue(sourceSystem, "SourceSystemType", "sourceSystemType"));
          const typeMatches = sourceSystemTypeValue === null || typeValue === normalizeSourceSystemType(sourceSystemTypeValue);
          const integrationMatches = !linkedIntegrationId || String(sourceIntegrationId).toLowerCase() === String(linkedIntegrationId).toLowerCase();
          return integrationMatches && typeMatches;
        });
        setSourceSystems(matchingSourceSystems);
        const linkedSourceSystem = linkedSourceSystemId
          ? loadedSourceSystems.find((sourceSystem) => String(getId(sourceSystem)).toLowerCase() === String(linkedSourceSystemId).toLowerCase())
          : null;

        if (linkedSourceSystem) {
          setSelectedSourceSystemId(getId(linkedSourceSystem));
          if (supportsExistingSourceSystemSelection) setMode(SOURCE_SYSTEM_MODE_EXISTING);
          setDraftSourceSystem((previous) => keepObjectStateWhenEqual(previous, {
            ...linkedSourceSystem,
            IsNew: false,
            IntegrationId: linkedIntegrationId || getValue(linkedSourceSystem, "IntegrationId", "integrationId") || "",
            SourceSystemType: sourceSystemTypeValue,
            ProfileId: getValue(linkedSourceSystem, "ProfileId", "profileId") || profileId
          }));
        } else {
          const firstMatchingSourceSystem = supportsExistingSourceSystemSelection ? getFirst(matchingSourceSystems) : null;
          if (firstMatchingSourceSystem && !linkedSourceSystemId) {
            setSelectedSourceSystemId(getId(firstMatchingSourceSystem));
            setMode(SOURCE_SYSTEM_MODE_EXISTING);
            setDraftSourceSystem((previous) => keepObjectStateWhenEqual(previous, {
              ...firstMatchingSourceSystem,
              IsNew: false,
              IntegrationId: linkedIntegrationId || getValue(firstMatchingSourceSystem, "IntegrationId", "integrationId") || "",
              SourceSystemType: sourceSystemTypeValue,
              ProfileId: getValue(firstMatchingSourceSystem, "ProfileId", "profileId") || profileId
            }));
            return;
          }
          if (linkedSourceSystemId) {
            setSelectedSourceSystemId("");
            setMode(SOURCE_SYSTEM_MODE_NEW);
            setDraftSourceSystem((previous) => keepObjectStateWhenEqual(previous, {
              ...nextDraftSeed,
              IsNew: true,
              IntegrationId: linkedIntegrationId,
              SourceSystemType: sourceSystemTypeValue,
              ProfileId: nextDraftSeed?.ProfileId || profileId
            }));
          } else {
            setSelectedSourceSystemId("");
            setMode(SOURCE_SYSTEM_MODE_NEW);
            setDraftSourceSystem((previous) => keepObjectStateWhenEqual(previous, mergeDraftWithSeed(previous, nextDraftSeed, previousSeed, {
              IsNew: !getId(previous),
              IntegrationId: linkedIntegrationId,
              SourceSystemType: sourceSystemTypeValue,
              ProfileId: previous?.ProfileId || profileId
            })));
          }
        }
      })
      .catch((ex) => {
        if (mounted) setLocalError(ex?.message || "Source System records could not be loaded.");
      })
      .finally(() => {
        if (mounted) setLoadingSourceSystems(false);
      });

    return () => {
      mounted = false;
      if (loadKeyRef.current === loadKey) {
        loadKeyRef.current = "";
      }
    };
    // The load key uses stable serialized wizard values/defaults to avoid a parent object-identity reload loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [integrationMgrReady, linkedIntegrationId, linkedSourceSystemId, productResourceDefaultsKey, profileId, sourceSystemDraftValuesKey, sourceSystemMgrReady, sourceSystemTypeValue, supportsExistingSourceSystemSelection]);

  const selectedSourceSystem = useMemo(
    () => sourceSystems.find((sourceSystem) => String(getId(sourceSystem)) === String(selectedSourceSystemId)) || null,
    [selectedSourceSystemId, sourceSystems]
  );

  const sourceSystemFormMetadata = useMemo(() => {
    const setupMetadata = applySetupFormFieldOverrides(metadata, getSetupFormFieldOverrides(productResourceDefaults, "SourceSystem"));
    return buildSetupFormMetadata(setupMetadata, {
      hiddenFieldNames: SOURCE_SYSTEM_SETUP_HIDDEN_FIELD_NAMES
    });
  }, [metadata, productResourceDefaults]);

  const sourceSystemFormFields = useMemo(
    () => getMetadataFields(sourceSystemFormMetadata),
    [sourceSystemFormMetadata]
  );

  const existingSourceSystemOptions = useMemo(
    () => sourceSystems.map((sourceSystem) => {
      const id = getId(sourceSystem);
      return {
        Text: getDisplayName(sourceSystem) || getValue(sourceSystem, "Name", "name") || id,
        Value: id,
        FilterValue: String(getValue(sourceSystem, "SourceSystemType", "sourceSystemType") ?? "")
      };
    }),
    [sourceSystems]
  );

  const updateSelectedSourceSystemId = useCallback((nextValue) => {
    const nextId = nextValue || "";
    const nextSourceSystem = sourceSystems.find((sourceSystem) => String(getId(sourceSystem)) === String(nextId)) || null;
    setSelectedSourceSystemId(nextId);
    if (nextSourceSystem) {
      setDraftSourceSystem((previous) => keepObjectStateWhenEqual(previous, {
        ...nextSourceSystem,
        IsNew: false,
        IntegrationId: linkedIntegrationId || getValue(nextSourceSystem, "IntegrationId", "integrationId") || "",
        SourceSystemType: sourceSystemTypeValue,
        ProfileId: getValue(nextSourceSystem, "ProfileId", "profileId") || profileId
      }));
    }
    setLocalError("");
  }, [linkedIntegrationId, profileId, sourceSystems, sourceSystemTypeValue]);

  const validateLinkedIntegration = useCallback(() => {
    if (!linkedIntegrationId) {
      const validationMessage = "Complete the Integration step before configuring the Source System.";
      setLocalError(validationMessage);
      throw buildWizardValidationError(validationMessage, { suppressSummary: true });
    }
  }, [linkedIntegrationId]);

  const validateDraftSourceSystem = useCallback((sourceSystem, groupId = SOURCE_SYSTEM_NEW_FORM_GROUP_ID) => {
    validateLinkedIntegration();
    if (!sourceSystemFormMetadata) {
      const validationMessage = "Source System form metadata is not available.";
      setLocalError(validationMessage);
      throw new Error(validationMessage);
    }
    if (hasAndShowValidationErrors(groupId)) {
      const validationMessage = "Complete the required Source System fields before continuing.";
      setLocalError(validationMessage);
      throw buildWizardValidationError(validationMessage, { suppressSummary: true });
    }
    const validationMessage = validateRequiredIntegrationFields({
      ...sourceSystem,
      IntegrationId: linkedIntegrationId,
      SourceSystemType: sourceSystemTypeValue,
      ProfileId: sourceSystem.ProfileId || profileId
    }, sourceSystemFormFields);
    if (validationMessage) {
      setLocalError(validationMessage);
      throw buildWizardValidationError(validationMessage, { suppressSummary: true });
    }
    setLocalError("");
  }, [hasAndShowValidationErrors, linkedIntegrationId, profileId, sourceSystemFormFields, sourceSystemFormMetadata, sourceSystemTypeValue, validateLinkedIntegration]);

  const saveResolvedSourceSystem = useCallback(async (sourceSystem, fallbackMessage = "Source System could not be saved.") => {
    const activeMgr = sourceSystemMgrRef.current;
    if (!activeMgr) throw new Error("Source System service is not available.");

    const sourceSystemToSave = {
      ...sourceSystem,
      IsNew: !getId(sourceSystem),
      IntegrationId: linkedIntegrationId,
      SourceSystemType: sourceSystemTypeValue,
      ProfileId: sourceSystem?.ProfileId || profileId
    };
    const saveResult = await activeMgr.saveItem(sourceSystemToSave);
    requireSuccessfulServiceResponse(saveResult, fallbackMessage, coreStateRef.current);

    const savedSourceSystem = getOperationItem(saveResult)
      || getFirst(saveResult?.Items)
      || getFirst(saveResult?.items)
      || saveResult?.Item
      || saveResult?.item
      || saveResult;
    if (!getId(savedSourceSystem)) throw new Error("Source System save did not return a Source System id.");

    setDraftSourceSystem(savedSourceSystem);
    return savedSourceSystem;
  }, [linkedIntegrationId, profileId, sourceSystemTypeValue]);

  const runLoadSourceSystemTargets = useCallback(async (sourceSystem) => {
    const activeMgr = sourceSystemMgrRef.current;
    if (!activeMgr) throw new Error("Source System service is not available.");

    setLoadingTargets(true);
    try {
      const result = await activeMgr.RunOperation({
        operationName: LOAD_SOURCE_SYSTEM_TARGETS_OPERATION,
        item: sourceSystem,
        queryParams: buildResourceOperationQueryParams({
          queryParams: { reload: true },
          wizard,
          item: sourceSystem
        }),
        isItem: true,
        isWarning: true,
        supressAllErrors: false
      });
      requireSuccessfulServiceResponse(result, "Source targets could not be generated.", coreStateRef.current);
      const operationResult = getOperationResult(result);
      if (result?.Success === false || operationResult?.Success === false) {
        throw buildServerFailureError(operationResult || result, "Source targets could not be generated.", coreStateRef.current);
      }
      const updatedSourceSystem = getFirst(operationResult?.Items)
        || getFirst(operationResult?.items)
        || operationResult?.Item
        || operationResult?.item
        || getFirst(result?.Items)
        || getFirst(result?.items)
        || sourceSystem;
      setDraftSourceSystem(updatedSourceSystem);
      return updatedSourceSystem;
    } finally {
      setLoadingTargets(false);
    }
  }, [wizard]);

  const confirmContinue = useCallback(() => new Promise((resolve) => {
    setTargetGenerationConfirm({ resolve });
  }), []);

  const closeTargetGenerationConfirm = useCallback((confirmed) => {
    const resolver = targetGenerationConfirm?.resolve;
    setTargetGenerationConfirm(null);
    resolver?.(confirmed);
  }, [targetGenerationConfirm]);

  const prepareStepValues = useCallback(async (currentValues = values, options = {}) => {
    const selectedExistingSourceSystem = mode === SOURCE_SYSTEM_MODE_EXISTING
      ? selectedSourceSystem
      : null;
    if (mode === SOURCE_SYSTEM_MODE_EXISTING && !selectedExistingSourceSystem) {
      const validationMessage = "Select a Source System before continuing.";
      setLocalError(validationMessage);
      throw buildWizardValidationError(validationMessage, { suppressSummary: true });
    }
    const sourceSystemToSave = {
      ...(selectedExistingSourceSystem || draftSourceSystem),
      IsNew: !getId(selectedExistingSourceSystem || draftSourceSystem),
      IntegrationId: linkedIntegrationId,
      SourceSystemType: sourceSystemTypeValue,
      ProfileId: (selectedExistingSourceSystem || draftSourceSystem)?.ProfileId || profileId
    };
    validateDraftSourceSystem(sourceSystemToSave, mode === SOURCE_SYSTEM_MODE_EXISTING ? SOURCE_SYSTEM_EXISTING_FORM_GROUP_ID : SOURCE_SYSTEM_NEW_FORM_GROUP_ID);
    let resolvedSourceSystem = await saveResolvedSourceSystem(sourceSystemToSave, "Source System could not be saved.");
    if (options.action === "continue") {
      resolvedSourceSystem = await runLoadSourceSystemTargets(resolvedSourceSystem);
    }

    return buildSourceSystemStepValues({
      currentValues,
      sourceSystem: resolvedSourceSystem,
      productResourceDefaults,
      integrationId: linkedIntegrationId
    });
  }, [
    draftSourceSystem,
    linkedIntegrationId,
    mode,
    productResourceDefaults,
    profileId,
    runLoadSourceSystemTargets,
    saveResolvedSourceSystem,
    selectedSourceSystem,
    sourceSystemTypeValue,
    validateDraftSourceSystem,
    values
  ]);

  confirmContinueRef.current = confirmContinue;
  prepareStepValuesRef.current = prepareStepValues;
  isBusyRef.current = loadingSourceSystems || loadingTargets;
  const canContinue = Boolean(
    linkedIntegrationId
    && sourceSystemFormMetadata
    && !loadingSourceSystems
    && !loadingTargets
    && (mode !== SOURCE_SYSTEM_MODE_EXISTING || selectedSourceSystemId)
  );

  useEffect(() => {
    registerStepController?.({
      continueLabel: "Save & Continue",
      canContinue: () => canContinue,
      confirmContinue: () => confirmContinueRef.current?.() ?? true,
      get isBusy() {
        return Boolean(isBusyRef.current);
      },
      prepareSave: (currentValues, actionContext) =>
        prepareStepValuesRef.current?.(currentValues, { ...actionContext, action: "save" }) ?? currentValues,
      prepareContinue: (currentValues, actionContext) =>
        prepareStepValuesRef.current?.(currentValues, { ...actionContext, action: "continue" }) ?? currentValues
    });
  }, [canContinue, registerStepController]);

  useEffect(() => () => registerStepController?.(null), [registerStepController]);

  const handleDraftFormChange = (value, fieldName, updatedItem) => {
    setDraftSourceSystem((previous) => keepObjectStateWhenEqual(previous, {
      ...(updatedItem || previous),
      [fieldName]: value,
      IntegrationId: linkedIntegrationId,
      SourceSystemType: sourceSystemTypeValue,
      ProfileId: getValue(updatedItem, "ProfileId", "profileId") || previous?.ProfileId || profileId
    }));
  };

  const handleSelectedSourceSystemFormChange = (value, fieldName, updatedItem) => {
    setSourceSystems((previous) => previous.map((sourceSystem) => {
      if (String(getId(sourceSystem)) !== String(selectedSourceSystemId)) return sourceSystem;
      const nextSourceSystem = {
        ...sourceSystem,
        ...(updatedItem || {}),
        [fieldName]: value,
        IntegrationId: linkedIntegrationId,
        SourceSystemType: sourceSystemTypeValue,
        ProfileId: getValue(updatedItem, "ProfileId", "profileId") || sourceSystem?.ProfileId || profileId
      };
      setDraftSourceSystem(nextSourceSystem);
      return nextSourceSystem;
    }));
  };

  const sourceSystemTypeLabel = sourceSystemTypeKey || (sourceSystemTypeValue !== null && sourceSystemTypeValue !== undefined ? sourceSystemTypeValue : "the required source type");

  return (
    <div className="resource-wizard__linked-step">
      <div className="resource-wizard__linked-step-header">
        <div>
          <div className="resource-wizard__linked-step-title-row">
            <h3>Source System</h3>
            {supportsExistingSourceSystemSelection ? (
              <div className="resource-wizard__mode-control" role="group" aria-label="Source System mode">
                <ButtonControl
                  type="standard-small"
                  className={`resource-wizard__mode-button${mode === SOURCE_SYSTEM_MODE_EXISTING ? " active" : ""}`}
                  onClick={() => {
                    setMode(SOURCE_SYSTEM_MODE_EXISTING);
                    setLocalError("");
                  }}
                  disabled={readOnly || loadingSourceSystems || sourceSystems.length === 0}
                >
                  Existing
                </ButtonControl>
                <ButtonControl
                  type="standard-small"
                  className={`resource-wizard__mode-button${mode === SOURCE_SYSTEM_MODE_NEW ? " active" : ""}`}
                  onClick={() => {
                    setMode(SOURCE_SYSTEM_MODE_NEW);
                    setLocalError("");
                  }}
                  disabled={readOnly}
                >
                  New
                </ButtonControl>
              </div>
            ) : null}
          </div>
          <p>
            {supportsExistingSourceSystemSelection
              ? `Use this ${providerName} Source System to define the ${sourceSystemTypeLabel} data boundary that will use the selected Integration. Select an existing EDI Source System or create a new one for this configured source bundle.`
              : `Use this ${providerName} Source System to define the ${sourceSystemTypeLabel} data boundary that will use the selected Integration. The setup creates and manages its own Source System for this configured source bundle.`}
          </p>
        </div>
      </div>

      {localError ? <div className="resource-wizard__alert">{localError}</div> : null}

      {!linkedIntegrationId ? (
        <div className="resource-wizard__state">Complete the Integration step before configuring the Source System.</div>
      ) : loadingSourceSystems ? (
        <div className="resource-wizard__linked-step-loading"><Loading /></div>
      ) : mode === SOURCE_SYSTEM_MODE_EXISTING && supportsExistingSourceSystemSelection ? (
        <div className="resource-wizard__linked-step-existing">
          <FieldControl
            disabled={readOnly || sourceSystems.length === 0}
            fieldMetadata={{
              FieldType: SystemTypes.UIFieldType.SearchSelectList,
              FieldName: "ResourceWizardSourceSystemId",
              DisplayName: "Existing Source System",
              Description: `${sourceSystemTypeKey || "Matching"} Source Systems on the selected Integration are available.`,
              IsRequired: true,
              DataJson: existingSourceSystemOptions,
              PlaceHolder: "Select a Source System"
            }}
            selectData={existingSourceSystemOptions}
            groupId={SOURCE_SYSTEM_EXISTING_FORM_GROUP_ID}
            value={selectedSourceSystemId || null}
            onChange={updateSelectedSourceSystemId}
          />
          {sourceSystems.length === 0 ? (
            <div className="resource-wizard__state">No matching EDI Source Systems are available for the selected Integration.</div>
          ) : null}
          {selectedSourceSystem && sourceSystemFormMetadata ? (
            <div className="resource-wizard__linked-step-form">
              <FormControl
                key={`source-system-existing-form-${selectedSourceSystemId}`}
                className="resource-wizard__linked-step-form-control"
                metadata={sourceSystemFormMetadata}
                formFields={sourceSystemFormFields}
                itemData={{
                  ...selectedSourceSystem,
                  IntegrationId: linkedIntegrationId,
                  SourceSystemType: sourceSystemTypeValue,
                  ProfileId: selectedSourceSystem?.ProfileId || profileId
                }}
                disableFooter={true}
                disableMinWidth={true}
                disabled={readOnly}
                groupId={SOURCE_SYSTEM_EXISTING_FORM_GROUP_ID}
                tabLayout="top"
                onChange={handleSelectedSourceSystemFormChange}
                onSave={() => {}}
              />
            </div>
          ) : null}
        </div>
      ) : (
        <div className="resource-wizard__linked-step-form">
          {sourceSystemFormMetadata ? (
            <FormControl
              className="resource-wizard__linked-step-form-control"
              metadata={sourceSystemFormMetadata}
              formFields={sourceSystemFormFields}
              itemData={{
                ...draftSourceSystem,
                IntegrationId: linkedIntegrationId,
                SourceSystemType: sourceSystemTypeValue,
                ProfileId: draftSourceSystem?.ProfileId || profileId
              }}
              disableFooter={true}
              disableMinWidth={true}
              disabled={readOnly}
              groupId={SOURCE_SYSTEM_NEW_FORM_GROUP_ID}
              tabLayout="top"
              onChange={handleDraftFormChange}
              onSave={() => {}}
            />
          ) : (
            <div className="resource-wizard__state">Source System metadata is not available.</div>
          )}
        </div>
      )}

      <DialogControl
        title="Generate Source Targets"
        openDialog={Boolean(targetGenerationConfirm)}
        okText="Continue"
        cancelText="Cancel"
        onOk={() => closeTargetGenerationConfirm(true)}
        onCancel={() => closeTargetGenerationConfirm(false)}
        onX={() => closeTargetGenerationConfirm(false)}
      >
        <p>Convoke will validate and save this Source System, then generate Source Targets from the selected provider. Do you want to continue?</p>
      </DialogControl>
    </div>
  );
};

export default SourceSystemStep;
